import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import botAvatar1 from "../assets/netmonk-logo.png";
import { useNavigate } from "react-router-dom";
import { hashString, encodeJWT } from "./conversion";
import {
  UserIcon,
  LockClosedIcon,
  EyeSlashIcon,
  EyeIcon,
} from "@heroicons/react/24/solid";
import { enc, SHA256 } from "crypto-js";
import { trackSuccessfulLogin } from "../GoogleAnalytics";

const Card = styled(motion.div)`
  border-radius: 30px;
  cursor: pointer;
`;

const Tooltip = styled.div`
  visibility: hidden;
  opacity: 0;
  width: 120px;
  background-color: #0056b3;
  color: white;
  text-align: center;
  border-radius: 6px;
  padding: 5px;
  position: absolute;
  z-index: 1;
  bottom: 110%; /* Position the tooltip above the button */
  left: 50%;
  margin-left: -60px;
  transition: opacity 0.3s;
  font-size: 13px;

  &::after {
    content: "";
    position: absolute;
    top: 100%; /* Arrow at the bottom of the tooltip */
    left: 50%;
    margin-left: -5px;
    border-width: 5px;
    border-style: solid;
    border-color: #0056b3 transparent transparent transparent;
  }
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100vh;
  background-color: #f2f3f5;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  background: white;
  padding: 30px 50px 50px 50px;
  border-radius: 30px;
  box-shadow: 0 0 10px rgba(0, 0, 0, 0.1);
`;

const InputContainer = styled.div`
  position: relative;
  width: 100%;
  margin-bottom: 20px;
`;

const Icon = styled.div`
  position: absolute;
  top: 50%;
  left: 10px;
  transform: translateY(-50%);
  color: #ccc;
`;

const Input = styled.input`
  width: 100%;
  padding: 15px 15px 15px 40px; /* Adjust padding to make space for the icon */
  border: 1px solid #ccc;
  border-radius: 10px;
`;

const Button = styled.button`
  padding: 10px;
  background-color: rgb(19, 71, 128);
  color: white;
  border: none;
  border-radius: 10px;
  cursor: pointer;

  &:hover {
    background-color: #0056b3;
  }

  &:disabled {
    background-color: #cccccc;
    color: #666666;
    cursor: not-allowed;
  }
`;

const ErrorMessage = styled.div`
  color: red;
  margin-bottom: 20px;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const TermsText = styled.p`
  font-size: 12px;
  color: #666;
  text-align: center;
  margin-top: 15px;
  line-height: 1.5;
  max-width: 400px;
`;

export const LoginLayout = ({ loginUrl }) => {
  const navigate = useNavigate();

  const [showPassword, setShowPassword] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loginError, setLoginError] = useState("");
  const [isAgreementChecked, setIsAgreementChecked] = useState(false);

  useEffect(() => {
    const accessToken = localStorage.getItem("access_token");
    const apiToken = localStorage.getItem("api_token");
    const tokenType = localStorage.getItem("token_type");
    const loginType = localStorage.getItem("login_type");
    if (accessToken && apiToken && tokenType && loginType) {
      navigate("/analytics");
    }
  }, []);

  const handleLogin = async (email, password) => {
    try {
      const token = await encodeJWT({
        email,
        prime_password: hashString(password, true),
        hi_password: SHA256(password).toString(enc.Base64),
      });

      const response = await axios.post(loginUrl, { token: token });

      if (response.status !== 200) {
        // console.log(response);
        throw new Error(response.status,"Invalid email or password");
      }

      return response.data;
    } catch (error) {
      switch (error.response.data.error.code) {
        case 401:
          setLoginError("Pastikan username dan password Anda sudah benar");
          break;
        case 403:
          setLoginError("Forbidden");
          break;
        case 404:
          setLoginError("Not found");
          break;
        case 500:
          setLoginError("Internal server error");
          break;
        default:
          setLoginError("Terjadi kesalahan, silahkan coba lagi");
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const result = await handleLogin(email, password);
    if (result) {
      const { access_token, token_type, login_type, api_token } = result;
      localStorage.setItem("access_token", access_token);
      localStorage.setItem("api_token", api_token);
      localStorage.setItem("token_type", token_type);
      localStorage.setItem("login_type", login_type);
      trackSuccessfulLogin();
      navigate("/analytics");
    }
  };

  return (
    <AnimatePresence>
      <motion.div
        className="fixed left-5 right-5 flex flex-col rounded-[1.8rem] bg-white font-lato"
        initial={{ y: 100, opacity: 0 }}
        animate={{ y: 0, opacity: 100 }}
        exit={{ opacity: 0 }}
        transition={{ type: "spring", stiffness: 100 }}
        key="login"
      >
        <Container>
          <Form onSubmit={handleSubmit}>
            <Card className="mb-5 ml-2 flex items-center justify-center p-2">
              <img className="h-20 w-20" src={botAvatar1} alt="Bot Logo" />
              <Tooltip className="tooltip">Kembali ke Home</Tooltip>
            </Card>
            <InputContainer>
              <Icon>
                <UserIcon className="h-5 w-5" />
              </Icon>
              <Input
                placeholder="Email atau Username"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </InputContainer>
            <InputContainer>
              <Icon>
                <LockClosedIcon className="h-5 w-5" />
              </Icon>
              <Input
                type={showPassword ? "text" : "password"}
                placeholder="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2"
              >
                {showPassword ? (
                  <EyeSlashIcon className="h-5 w-5" />
                ) : (
                  <EyeIcon className="h-5 w-5" />
                )}
              </button>
            </InputContainer>
            {loginError && <ErrorMessage>{loginError}</ErrorMessage>}
            <Button type="submit" disabled={!isAgreementChecked}>
              Login
            </Button>
            <TermsText>
              <input
                type="checkbox"
                value={isAgreementChecked}
                onChange={() => {
                  setIsAgreementChecked(!isAgreementChecked);
                }}
                style={{ marginRight: "0.5rem" }}
              />
              Saya setuju dengan{" "}
              {/* I agree to the{" "} */}
              <a
                href="https://netmonk.id/help/articles/%5Badm-kebijakan-penggunaan-2%5Dsyarat-%26-kebijakan-penggunaan-netmonk-prime"
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: "rgba(16,114,186)" }}
              >
                Syarat Penggunaan
                {/* Terms of Use */}
              </a>{" "}
              dan{" "}
              {/* and{" "} */}
              <a
                href="https://netmonk.id/help/articles/%5Badm-kebijakan-privasi-2%5Dkebijakan-privasi-penggunaan-netmonk-prime"
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: "rgba(16,114,186)" }}
              >
                Kebijakan Privasi{' '}
                {/* Privacy Policy */}
              </a>
              
              untuk menghubungi saya dan menciptakan pengalaman pelanggan yang lebih baik.
              {/* to contact me and create a better customer experience. */}
            </TermsText>
          </Form>
        </Container>
      </motion.div>
    </AnimatePresence>
  );
};

LoginLayout.defaultProps = {
  loginUrl:
    process.env.REACT_APP_BASE_URL + "/api-chatbot-builder/widget/auth/token",
};
